import { useQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { EventTimeline, SpecialProperty, TimelineEvent } from "@/components/timeline";
import { AssistantSlackMessageSummarySchema } from "@/domains/openapi/types";
import { api } from "@/modules/api";

interface AssistantSlackMessagesTimelineProps {
  sessionId: string;
}

const asString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const truncate = (value: string, maxLength: number) =>
  value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;

const buildSummary = (message: AssistantSlackMessageSummarySchema): string => {
  const parts: string[] = [];
  if (message.channel_id) {
    parts.push(message.channel_id);
  }
  if (message.sender_type) {
    parts.push(message.sender_type);
  }
  if (message.task_count > 0) {
    parts.push(`${message.task_count} ${message.task_count === 1 ? "task" : "tasks"}`);
  }
  if (message.reminder_count > 0) {
    parts.push(
      `${message.reminder_count} ${message.reminder_count === 1 ? "reminder" : "reminders"}`
    );
  }
  if (message.thread_count > 0) {
    parts.push(`${message.thread_count} ${message.thread_count === 1 ? "thread" : "threads"}`);
  }
  if (message.text) {
    parts.push(`"${truncate(message.text, 240)}"`);
  }
  return parts.join(" | ");
};

const resolveOccurredAt = (message: AssistantSlackMessageSummarySchema): string => {
  return message.message_at ?? message.created_at;
};

const buildEvents = (messages: AssistantSlackMessageSummarySchema[]): TimelineEvent[] => {
  return messages.map(message => ({
    id: message.id,
    kind: "slack_message",
    occurred_at: resolveOccurredAt(message),
    summary: buildSummary(message),
    properties_json: {
      channel_id: message.channel_id,
      channel_type: message.channel_type,
      message_ts: message.message_ts,
      message_at: message.message_at,
      text: message.text,
      sender_type: message.sender_type,
      slack_user_id: message.slack_user_id,
      bot_id: message.bot_id,
      task_count: message.task_count,
      reminder_count: message.reminder_count,
      thread_count: message.thread_count,
      threads: message.threads ?? [],
    },
  }));
};

const getSpecialProperties = (properties: Record<string, unknown>): SpecialProperty[] => {
  const threads = Array.isArray(properties.threads) ? properties.threads : [];
  const entries: (SpecialProperty | null)[] = threads.map(thread => {
    if (!thread || typeof thread !== "object") {
      return null;
    }
    const threadId = asString((thread as { id?: unknown }).id);
    if (!threadId) {
      return null;
    }
    return {
      key: `thread:${threadId}`,
      label: "Thread",
      id: threadId,
      route: atlasRoutes.assistantThreadsView({ params: { threadId } }).path,
    };
  });
  return entries.filter((value): value is SpecialProperty => Boolean(value));
};

export const AssistantSlackMessagesTimeline = ({
  sessionId,
}: AssistantSlackMessagesTimelineProps) => {
  const query = useQuery({
    queryKey: ["assistant-slack-messages", sessionId],
    queryFn: () =>
      api.post("/internal/v1/assistant-slack-messages/search", {
        body: {
          session_id: sessionId,
          limit: 200,
          offset: 0,
        },
      }),
    enabled: Boolean(sessionId),
    staleTime: 30 * 1000,
  });

  const messages = query.data?.data?.results ?? [];
  const total = query.data?.data?.total ?? 0;

  return (
    <EventTimeline<TimelineEvent>
      events={buildEvents(messages)}
      total={total}
      isLoading={query.isLoading}
      isError={query.isError}
      emptyMessage="No Slack messages recorded yet."
      getSpecialProperties={getSpecialProperties}
      sortDirection="desc"
      title="Slack Messages"
    />
  );
};
